import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sxDemonstrativoSubtitle } from './sxProps';
import './styles.css'
import LoadingApp from '../../../components/LoadingApp';
import { getBoleto, getDemonstrativo, getListaFaturas, getTitulos } from '../../../services/FinanceiroService'
import { base64toBlob } from '../../../utils/Base64ToBlob';
import { Titulo } from '../../../interfaces/entities/Titulo';
import { BoletoProtheus } from '../../../interfaces/entities/BoletoProtheus';
import ErrorModal from './ErrorModal';
import { getBenef } from '../../../Store/storages/BeneficiarioStorage';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import PageTitle from '../../../components/Accessibility/PageTitle';
import AbaHistorico from './AbaHistorico';
import AbaFatura from './AbaFatura';

function DemonstrativoFatura() {

  const [titulos, setTitulos] = useState<Titulo[]>([]);
  const [faturas, setFaturas] = useState<BoletoProtheus[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loadingDemonstrativo, setLoadingDemonstrativo] = useState<boolean>(false);
  const [loadingHistorico, setLoadingHistorico] = useState<boolean>(true);
  const [loadingFaturas, setLoadingFaturas] = useState<boolean>(true);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isTitular, setIsTitular] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'historico' | 'fatura'>('fatura');

  const accessibility = useAccessibility();
  const hasLoadedData = useRef(false);
  const paginationLength = 10;

  const reduceDuplicates = <T extends Record<string, any>, K extends keyof T>(items: T[], key: K) => {
    const uniqueItems = new Map<string, T>();

    items.forEach((item) => {
      const value = item[key];
      const normalizedValue = value === null || value === undefined ? '' : String(value);

      if (!normalizedValue || uniqueItems.has(normalizedValue)) {
        return;
      }

      uniqueItems.set(normalizedValue, item);
    });

    return Array.from(uniqueItems.values());
  };

  const reduceFaturaDuplicates = (items: BoletoProtheus[]) => {
    const uniqueItems = new Map<string, BoletoProtheus>();

    items.forEach((item) => {
      const numero = item?.numero ?? '';
      const parcela = item?.parcela ?? '';
      const normalizedKey = `${numero.trim()}|${String(Number(parcela) || parcela).trim()}`;

      if (!numero || uniqueItems.has(normalizedKey)) {
        return;
      }

      uniqueItems.set(normalizedKey, item);
    });

    return Array.from(uniqueItems.values());
  };

  useEffect(() => {
    if (hasLoadedData.current) {
      return;
    }

    hasLoadedData.current = true;

    const benef = getBenef();
    const titular = benef?.tipoUsr === 'TITULA'

    setIsTitular(titular);

    if (!titular) {
      setLoadingHistorico(false);
      setLoadingFaturas(false);
      return;
    }

    getTitulos()
      .then(response => {
        if (response) {
          setTitulos(reduceDuplicates(response, 'idTitulo'));
        }
      })
      .catch(error => {
        console.log(error);
        setTitulos([]);
      })
      .finally(() => {
        setLoadingHistorico(false);
      });

    getListaFaturas()
      .then(response => {
        setFaturas(reduceFaturaDuplicates(response || []));
      })
      .catch(error => {
        console.log(error);
        setFaturas([]);
      })
      .finally(() => {
        setLoadingFaturas(false);
      });

  }, []);

  const onVisualizarClicked = (id: string) => {
    setLoadingDemonstrativo(true);

    getDemonstrativo(id)
      .then(response => {
        if (response?.data) {
          const pdf = response.data;
          const blob = base64toBlob(pdf.split(',')[1]);
          const blobUrl = URL.createObjectURL(blob);

          window.open(blobUrl);
        }
      })
      .catch(response => {
        const message = response?.data?.mensagem;

        setErrorMessage(message ? message : 'Erro ao carregar demonstrativo');
        setErrorModal(true);
      })
      .finally(() => {
        setLoadingDemonstrativo(false);
      });
  }

  const onDownloadFaturaClicked = async (id: string) => {
    try {
      setLoadingDemonstrativo(true);

      const fatura = faturas.find(item => item.numero === id);

      if (!fatura) {
        return;
      }

      const response = await getBoleto(
        fatura.prefixo,
        fatura.numero,
        fatura.parcela,
        'FAT'
      );

      const pdfBase64 = response?.base64 || fatura.base64;

      if (!pdfBase64) {
        setLoadingDemonstrativo(false);
        throw new Error('Fatura não disponível');
      }

      const content = pdfBase64.includes(',')
        ? pdfBase64.split(',')[1]
        : pdfBase64;

      const blob = base64toBlob(content);
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = `fatura-${fatura.numero}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
      setLoadingDemonstrativo(false);
    } catch (error) {
      console.log(error);
      setErrorMessage('Erro ao baixar fatura');
      setErrorModal(true);
      setLoadingDemonstrativo(false);
    }
  }

  const handleCloseErrorModal = () => {
    setErrorModal(false);
  }

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className='demonstrativo-container'>
      <PageTitle marginBottom="15px" text="Demonstrativo de fatura" />

      <LoadingApp showLoading={loadingDemonstrativo} />

      <ErrorModal open={errorModal} errorMessage={errorMessage} handleCloseModal={handleCloseErrorModal} />

      {isTitular &&
        <Typography style={accessibility.textStyleAccessibility} sx={sxDemonstrativoSubtitle}>
          Confira o valor da sua mensalidade.
        </Typography>
      }

      <div className='tabs-container'>
        <button
          className={`tab-button ${activeTab === 'fatura' ? 'active' : ''}`}
          onClick={() => {
            setPage(1);
            setActiveTab('fatura');
          }}
        >
          Fatura
        </button>

        <button
          className={`tab-button ${activeTab === 'historico' ? 'active' : ''}`}
          onClick={() => {
            setPage(1);
            setActiveTab('historico');
          }}
        >
          Histórico
        </button>
      </div>

      {activeTab === 'historico' ? (
        <AbaHistorico
          titulos={titulos}
          loading={loadingHistorico}
          page={page}
          onPageChange={handlePageChange}
          onVisualizarClicked={onVisualizarClicked}
          paginationLength={paginationLength}
        />
      ) : (
        <AbaFatura
          faturas={faturas}
          loading={loadingFaturas}
          page={page}
          onPageChange={handlePageChange}
          onDownloadFaturaClicked={onDownloadFaturaClicked}
          paginationLength={paginationLength}
        />
      )}

      {!titulos.length && !faturas.length && !loadingHistorico && !loadingFaturas && (
        <div className='container-sem-demonstrativo'>
          <FontAwesomeIcon size='5x' color='#00995D' icon={faExclamationCircle} />

          <Typography style={accessibility.textStyleAccessibility} sx={sxDemonstrativoSubtitle}>
            {activeTab === 'fatura'
              ? 'Nenhuma fatura encontrada.'
              : 'Nenhum histórico encontrado.'}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default DemonstrativoFatura

